/**
 * Typed accessor for the runtime configuration loaded from /config/config.json
 * at app startup (see main.ts -> loadConfig).
 *
 * Nothing environment-specific is baked into the JS bundle: the SAME build is
 * deployed to every environment, and config.json is injected per environment at
 * deploy time (from SSM Parameter Store via ssm_variables.json). This is what
 * lets one artifact serve prod, staging, and every customer tenant.
 */
import { getGlobalConfig } from "./config.js";

export interface AppConfig {
  // Cognito / auth
  aws_cognito_identity_pool_id?: string;
  aws_cognito_region?: string;
  aws_user_pools_id: string;
  aws_user_pools_web_client_id: string;
  domain_name?: string;
  oauth_domain: string;
  redirect_sign_in: string;
  redirect_sign_out: string;
  supported_logins: string;
  // Application
  mapbox_access_token?: string;
  support_email?: string;
  s3_upload_bucket?: string;
  // reCAPTCHA v2 checkbox site key (dormant rollback path; empty = disabled).
  recaptcha_site_key?: string;
  // AWS WAF CAPTCHA (always-on sign-in protection): the web ACL's Application
  // Integration URL and CAPTCHA JS API key. Either empty disables the puzzle
  // popup (a friendly message is shown when WAF challenges instead).
  waf_captcha_integration_url?: string;
  waf_captcha_api_key?: string;
  // Optional: seconds a solved CAPTCHA token stays accepted (the web ACL's
  // captcha immunity time). Client-side UX hint only; code defaults to 300.
  waf_captcha_immunity_seconds?: number;
  // How long an emailed set-password link stays valid, for the copy shown to
  // users. Must match the backend's PASSWORD_RESET_TOKEN_TTL_DAYS.
  reset_link_validity_days?: number;
}

const REQUIRED_KEYS: (keyof AppConfig)[] = [
  "aws_user_pools_id",
  "aws_user_pools_web_client_id",
  "oauth_domain",
  "redirect_sign_in",
  "redirect_sign_out",
  "supported_logins",
];

/** Throws (failing app startup loudly) if the runtime config is missing keys. */
export function validateConfig(config: unknown): void {
  if (!config || typeof config !== "object") {
    throw new Error("Runtime config (/config/config.json) failed to load.");
  }
  const cfg = config as Record<string, unknown>;
  const missing = REQUIRED_KEYS.filter((k) => !cfg[k]);
  if (missing.length) {
    throw new Error(`Runtime config is missing required keys: ${missing.join(", ")}`);
  }
}

export function getConfig(): AppConfig {
  return (getGlobalConfig() || {}) as AppConfig;
}

/** Mapbox public access token (a publishable pk.* token) from runtime config. */
export function getMapboxToken(): string {
  return getConfig().mapbox_access_token || "";
}

/** Support contact email from runtime config. */
export function getSupportEmail(): string {
  return getConfig().support_email || "";
}

/** Bucket holding project uploads (used to validate s3:// upload URLs). */
export function getUploadBucket(): string {
  return getConfig().s3_upload_bucket || "";
}

/**
 * reCAPTCHA v2 checkbox site key from runtime config. Empty string means the
 * captcha feature is OFF (no widget, no gating, no script loaded).
 */
export function getRecaptchaSiteKey(): string {
  return getConfig().recaptcha_site_key || "";
}

/**
 * Days an emailed set-password link stays valid.
 *
 * This is OUR ticket, not Cognito's code, which is why it can be days at all:
 * Cognito fixes ForgotPassword codes at one hour and exposes no setting for it.
 * The backend owns the real lifetime in PASSWORD_RESET_TOKEN_TTL_DAYS — this
 * key only controls the sentence shown to the user, so the two must be changed
 * together or the page will promise a window the backend does not honour.
 */
export function getResetLinkValidityDays(): number {
  const value = Number(getConfig().reset_link_validity_days);
  return Number.isFinite(value) && value > 0 ? value : 3;
}

/** How long a set-password link lasts, phrased for users. */
export function describeResetLinkValidity(): string {
  const days = getResetLinkValidityDays();
  return days === 1 ? "1 day" : `${days} days`;
}
